import React, { useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { ChevronRightIcon, MenuIcon, PhoneIcon, XIcon } from "lucide-react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useSite } from "../context/SiteContext";
import { locations, type LocationId } from "../data/locations";
import { CalaveraBadge, HeaderEmblem } from "./culture/HeaderEmblem";
import { PapelPicado } from "./culture/PapelPicado";
const primaryLinks: [string, string, string][] = [["/our-story", "About", "Nosotros"], ["/catering", "Events & Catering", "Eventos y Catering"], ["/locations", "Locations", "Ubicaciones"], ["/contact", "Contact", "Contacto"]];
const secondaryLinks: [string, string, string][] = [["/events", "Latin Night & Events", "Noche Latina y eventos"], ["/private-events", "Private Events", "Eventos privados"]];
export function Header() {
  const [open, setOpen] = useState(false);
  const [tucked, setTucked] = useState(false);
  const {
    scrollY
  } = useScroll();
  useMotionValueEvent(scrollY, "change", value => setTucked(value > 24));
  const {
    language,
    setLanguage,
    selectedLocation,
    setSelectedLocation,
    t
  } = useSite();
  const navigate = useNavigate();
  const location = locations[selectedLocation];
  const changeLocation = (value: string) => {
    const id = value as LocationId;
    setSelectedLocation(id);
    navigate(locations[id].pagePath);
    setOpen(false);
  };
  return <header className="sticky top-0 z-50 text-agavedeep" data-id="element-181" data-mp="3abpp1oiu741f">
      {/* Layered surface: warm cream, marigold glow behind the emblem, faint Talavera tile texture */}
      <div className="relative overflow-hidden bg-cream" data-id="element-182" data-mp="35jg12e1a8e1s">
        <div aria-hidden="true" className="absolute inset-0" style={{
        backgroundImage: "radial-gradient(circle at 44px 50%, rgba(232,135,30,.32), rgba(232,135,30,0) 160px)"
      }} data-id="element-183" data-mp="30lemo6esx2wn" />
        <svg aria-hidden="true" className="absolute inset-0 h-full w-full opacity-[0.07]" data-id="element-184" data-mp="3t9k70s1y0pvy">
          <defs data-id="element-185" data-mp="1rew3m5te85lc">
            <pattern id="header-talavera" width="24" height="24" patternUnits="userSpaceOnUse" data-id="element-186" data-mp="1recxdmd3v5n5">
              {[0, 90, 180, 270].map(angle => <path key={angle} d="M12 2C14.5 6 14.5 8 12 11C9.5 8 9.5 6 12 2Z" fill="#1F4E9C" transform={`rotate(${angle} 12 12)`} data-id="element-187" data-mp="1ho9xr3bhatfu" />)}
              <circle cx="0" cy="0" r="3" fill="#1F4E9C" data-id="element-188" data-mp="1ml0dtqna9p19" />
              <circle cx="24" cy="0" r="3" fill="#1F4E9C" data-id="element-189" data-mp="18mtudfj2zrl4" />
              <circle cx="0" cy="24" r="3" fill="#1F4E9C" data-id="element-190" data-mp="18pg0rbu8k11r" />
              <circle cx="24" cy="24" r="3" fill="#1F4E9C" data-id="element-191" data-mp="1cpgknc03t4g7" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#header-talavera)" data-id="element-192" data-mp="yq7aasykx4k5" />
        </svg>
      <div className="relative mx-auto flex h-[62px] max-w-7xl items-center justify-between gap-3 px-4 sm:px-6" data-id="element-193" data-mp="13p1sbsoq2p37">
        <HeaderEmblem data-id="element-194" data-mp="2t487l9ehdji4" />

        <nav className="hidden items-center gap-7 lg:flex" aria-label={t("Main navigation", "Navegación principal")} data-id="element-195" data-mp="2t0u6e4hjzgc9">
          {primaryLinks.map(([to, en, es]) => <NavLink key={to} to={to} className={({
            isActive
          }) => `whitespace-nowrap text-sm font-semibold transition-colors hover:text-chili focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marigold ${isActive ? "text-chili" : "text-agavedeep"}`} data-id="element-196" data-mp="mfg48nv31rfx">
              {t(en, es)}
            </NavLink>)}
        </nav>

        <div className="flex flex-none items-center gap-2" data-id="element-197" data-mp="1578u49euuwjf">
          <CalaveraBadge className="h-11 w-11 sm:h-12 sm:w-12" data-id="element-198" data-mp="1051ffc2n52de" />
          <div className="hidden min-h-10 items-center rounded-full border border-agavedeep/20 px-1 text-xs font-bold lg:flex" aria-label="Language selector" data-id="element-199" data-mp="1f325hpgu8jzm">
            {(["en", "es"] as const).map(code => <button key={code} onClick={() => setLanguage(code)} aria-pressed={language === code} className={`min-h-8 min-w-8 rounded-full uppercase ${language === code ? "bg-agavedeep text-cream" : "text-agavedeep"}`} data-id="element-200" data-mp="3gu6ximmonmkh">
                {code}
              </button>)}
          </div>
          <Link to="/locations" className="hidden min-h-11 items-center whitespace-nowrap rounded-full bg-marigold px-5 text-sm font-bold text-agavedeep shadow-[0_3px_0_#B8641A] transition-[background-color,transform] duration-150 hover:bg-sunflower active:translate-y-[2px] active:shadow-[0_1px_0_#B8641A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-agavedeep sm:inline-flex" data-id="element-201" data-mp="2cwv161k5h7ep">
            {t("Visit Us", "Visítanos")}
          </Link>
          <button onClick={() => setOpen(!open)} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-agavedeep/20 bg-cream/80 lg:hidden" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t("Close menu", "Cerrar menú") : t("Open menu", "Abrir menú")} data-id="element-202" data-mp="1xzkh43tfyt7d">
            {open ? <XIcon className="h-5 w-5" data-id="element-203" data-mp="xxo1206ori0r" /> : <MenuIcon className="h-5 w-5" data-id="element-204" data-mp="t0xf4w879s7f" />}
          </button>
        </div>
      </div>
      </div>

      {/* Serape-style border: Talavera blue, marigold, chili */}
      <div aria-hidden="true" data-id="element-205" data-mp="p16pivueti4h">
        <div className="h-[3px] bg-talavera" data-id="element-206" data-mp="k0966fxnyi1e" />
        <div className="h-[2px] bg-marigold" data-id="element-207" data-mp="jzhaz67zambn" />
        <div className="h-px bg-chili" data-id="element-208" data-mp="f0mlxmjf41j3" />
      </div>

      {/* Layered, dimensional papel picado that hangs from the header and tucks away on scroll */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-full h-14 overflow-hidden" data-id="element-209" data-mp="a8mn5xf33eo3">
        <motion.div initial={false} animate={tucked || open ? {
        y: -56,
        opacity: 0
      } : {
        y: 0,
        opacity: 1
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative h-full" data-id="element-210" data-mp="590d1wujyeji">
          <PapelPicado size="sm" depth offset showString={false} className="absolute inset-x-0 top-0 origin-top scale-[0.82] opacity-60" data-id="element-211" data-mp="3ngg0vwy3p4y1" />
          <PapelPicado size="sm" depth className="absolute inset-x-0 top-0" data-id="element-212" data-mp="3drslmjd0gx4p" />
        </motion.div>
      </div>

      <AnimatePresence data-id="element-213" data-mp="3iq3v2sze95me">
        {open && <motion.div id="mobile-menu" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-x-0 top-full max-h-[calc(100svh-68px)] overflow-y-auto border-b border-agavedeep/10 bg-cream pb-28 shadow-xl lg:hidden" data-id="element-214" data-mp="33vfwlysev7ga">
            <PapelPicado size="sm" animated={false} data-id="element-215" data-mp="3msqouzfml0m8" />
            <nav className="px-5 pt-4" aria-label={t("Navigation menu", "Menú de navegación")} data-id="element-216" data-mp="3rsvat4zaynju">
              {primaryLinks.map(([to, en, es]) => <NavLink key={to} to={to} onClick={() => setOpen(false)} className={({
            isActive
          }) => `flex min-h-14 items-center justify-between border-b border-agavedeep/10 font-serif text-[28px] font-bold ${isActive ? "text-chili" : "text-agavedeep"}`} data-id="element-217" data-mp="jc9xxd3jvabg">
                  {t(en, es)}
                  <ChevronRightIcon className="h-5 w-5 text-marigold" aria-hidden="true" data-id="element-218" data-mp="2ij74luotsyfg" />
                </NavLink>)}
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2" data-id="element-219" data-mp="1pvbcl3a7bup0">
                {secondaryLinks.map(([to, en, es]) => <Link key={to} to={to} onClick={() => setOpen(false)} className="min-h-10 py-2 text-sm font-semibold text-agavedeep/75 underline-offset-4 hover:underline" data-id="element-220" data-mp="1kx1fuo8twm1a">
                    {t(en, es)}
                  </Link>)}
              </div>
            </nav>
            <div className="mt-6 grid grid-cols-2 gap-3 px-5" data-id="element-221" data-mp="3aawkbl6q58fl">
              <select value={selectedLocation} onChange={event => changeLocation(event.target.value)} className="min-h-11 rounded-full border border-agavedeep/20 bg-white px-4 text-sm font-semibold text-agavedeep" aria-label={t("Choose location", "Elige ubicación")} data-id="element-222" data-mp="3ad96ybnfg6yz">
                <option value="driggs" data-id="element-223" data-mp="1dktpw2x8vqhi">Driggs</option>
                <option value="victor" data-id="element-224" data-mp="1dj8wwhqkwflr">Victor</option>
              </select>
              <div className="flex items-center rounded-full border border-agavedeep/20 bg-white p-1 text-sm font-bold" data-id="element-225" data-mp="1idd3v0zugnmu">
                {(["en", "es"] as const).map(code => <button key={code} onClick={() => setLanguage(code)} aria-pressed={language === code} className={`min-h-9 flex-1 rounded-full uppercase ${language === code ? "bg-agavedeep text-cream" : "text-agavedeep"}`} data-id="element-226" data-mp="1rbgnfrr9rac0">
                    {code}
                  </button>)}
              </div>
            </div>
            <a href={location.phoneHref} className="mx-5 mt-4 flex min-h-12 items-center justify-center gap-2 rounded-full bg-agavedeep font-bold text-cream" data-id="element-227" data-mp="6yr86intrt2h">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-228" data-mp="2ff57jsfrqpqv" />
              {location.phone}
            </a>
          </motion.div>}
      </AnimatePresence>
    </header>;
}